import { Sparkles } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import type { AlertSummary } from '@/features/alerts/types'
import type { CaseDetail } from '../types'

/**
 * AI Fraud Copilot Summary — narrative card from the catalogue's
 * Investigation Workspace mock. Surfaces a synthesized one-screen
 * read of the case so the analyst doesn't have to chain through the
 * timeline + linked alerts to know what's happening.
 *
 * Source order:
 *   1. The case's manually-edited summary (c.summary), if set.
 *   2. A deterministic auto-summary derived from linked alerts —
 *      the highest-priority signal, the count, the channel mix,
 *      and the time window. Worded to match the catalogue's voice.
 *
 * The auto-summary is best-effort and explicitly labelled as
 * "auto-generated"; an analyst can override it via the case-edit
 * surface (existing handler) which sets c.summary.
 */
export function AICopilotSummaryCard({ c }: { c: CaseDetail }) {
  const summary = c.summary || synthesizeSummary(c)
  const isAuto = !c.summary
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={14} className="text-primary" aria-hidden />
            AI Fraud Copilot summary
          </CardTitle>
          {isAuto ? (
            <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-primary">
              auto-generated
            </span>
          ) : (
            <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-muted-fg">
              analyst note
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-line text-sm leading-relaxed">{summary}</p>
      </CardContent>
    </Card>
  )
}

// synthesizeSummary builds a deterministic narrative from the case's
// linked alerts so first-load case detail isn't a blank "no summary"
// state. Mirrors the catalogue's voice — opens with the typology, lists
// the channels involved, totals the amount, and ends with a short
// recommendation.
function synthesizeSummary(c: CaseDetail): string {
  const alerts = c.linked_alerts
  if (alerts.length === 0) {
    return 'No linked alerts yet — pull a related alert into this case to seed the AI copilot summary.'
  }
  const top = topAlert(alerts)
  const channels = uniqueChannels(alerts)
  const totalNaira = totalAmountNaira(alerts)
  const window = describeWindow(alerts)

  const parts: string[] = []
  if (top) {
    const scenario = (top as unknown as { scenario_name?: string; alert_type?: string }).scenario_name ??
      (top as unknown as { alert_type?: string }).alert_type ??
      'High-risk activity'
    parts.push(`Detected ${scenario.toLowerCase()} pattern across ${alerts.length} linked alert${alerts.length === 1 ? '' : 's'}.`)
  } else {
    parts.push(`${alerts.length} linked alert${alerts.length === 1 ? '' : 's'} on this case.`)
  }
  if (channels.length > 0) {
    const chList = channels.slice(0, 3).map((c) => c.replace(/_/g, ' ')).join(', ')
    parts.push(
      `Channel${channels.length > 1 ? 's' : ''} involved: ${chList}${channels.length > 3 ? ` and ${channels.length - 3} more` : ''}.`,
    )
  }
  if (totalNaira > 0) {
    parts.push(`Total exposure: ${formatNaira(totalNaira)}${window ? ` over ${window}` : ''}.`)
  } else if (window) {
    parts.push(`Activity window: ${window}.`)
  }
  parts.push(
    'Recommended next step: validate the AI signal against KYC + recent profile changes, then proceed with disposition.',
  )
  return parts.join(' ')
}

function topAlert(alerts: AlertSummary[]): AlertSummary | null {
  let top: AlertSummary | null = null
  let topScore = -1
  for (const a of alerts) {
    const s = (a as unknown as { risk_score?: number }).risk_score ?? 0
    if (s > topScore) {
      topScore = s
      top = a
    }
  }
  return top
}

function uniqueChannels(alerts: AlertSummary[]): string[] {
  const set = new Set<string>()
  for (const a of alerts) {
    const c = (a as unknown as { transaction?: { channel?: string } }).transaction?.channel
    if (c) set.add(c)
  }
  return Array.from(set)
}

function totalAmountNaira(alerts: AlertSummary[]): number {
  let total = 0
  for (const a of alerts) {
    const m = (a as unknown as { transaction?: { amount_minor?: number } }).transaction?.amount_minor
    if (typeof m === 'number') total += m
  }
  return Math.round(total / 100)
}

function describeWindow(alerts: AlertSummary[]): string | null {
  if (alerts.length === 0) return null
  let min = Number.POSITIVE_INFINITY
  let max = 0
  for (const a of alerts) {
    const t = new Date((a as unknown as { created_at: string }).created_at).getTime()
    if (t < min) min = t
    if (t > max) max = t
  }
  const spanMs = max - min
  if (!Number.isFinite(spanMs) || spanMs <= 0) return null
  const spanHours = Math.round(spanMs / 3_600_000)
  if (spanHours < 1) return 'less than an hour'
  if (spanHours < 24) return `${spanHours}h`
  return `${Math.round(spanHours / 24)}d`
}

function formatNaira(n: number): string {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n)
}
